import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Updated to useNavigate

const ManageProducts = ({ backendUrl, resetKey }) => {
  const [products, setProducts] = useState([]);
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]); // Categories from backend
  const [newProduct, setNewProduct] = useState({
    title: '',
    price: '',
    event: '',
    status: true,
    featuredImage: '',
    featuredVideo: '',
    category: [], // Selected category IDs
    description: '',
    isPublic: false, // NEW: default false
    available_in_apk_launcher: false,
    cardPaymentEnabled: false,
  });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [titleFilter, setTitleFilter] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(''); // Filter by category
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      await fetchProducts();
      await fetchEvents();
      await fetchCategories();
      setSelectedProduct(null);
      setShowCreateForm(false);
      setNewProduct({
        title: '',
        price: '',
        event: '',
        status: true,
        featuredImage: '',
        featuredVideo: '',
        category: [],
        description: '',
        isPublic: false, // reset
        available_in_apk_launcher: false,
        cardPaymentEnabled: false,
      });
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const fetchProducts = async () => {
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/product/get`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
      });
      const result = await response.json();
      if (result.success) {
        setProducts(result.products || []);
      } else {
        alert('Error fetching Products');
      }
    } catch (error) {
      console.error('Error fetching Products:', error);
    }
  };

  const fetchEvents = async () => {
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/event/get`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
      });
      const result = await response.json();
      if (result.success) {
        setEvents(result.events || []);
      } else {
        alert('Error fetching Events');
      }
    } catch (error) {
      console.error('Error fetching Events:', error);
    }
  };

  const fetchCategories = async () => {
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/category/get`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
      });
      const result = await response.json();
      if (result.success) {
        setCategories(result.categories || []);
      } else {
        alert('Error fetching Categories');
      }
    } catch (error) {
      console.error('Error fetching Categories:', error);
    }
  };

  const handleNewProductChange = (e) => {
    const { name, value } = e.target;
    setNewProduct({ ...newProduct, [name]: value });
  };

  const handleSelectedProductChange = (e) => {
    const { name, value } = e.target;
    setSelectedProduct({ ...selectedProduct, [name]: value });
  };

  const handleCategoryChange = (e) => {
    const { value } = e.target;
    if (!newProduct.category.includes(value) && value) {
      setNewProduct((prevProduct) => ({
        ...prevProduct,
        category: [...prevProduct.category, value],
      }));
    }
  };

  const handleCategoryRemove = (categoryId) => {
    setNewProduct((prevProduct) => ({
      ...prevProduct,
      category: prevProduct.category.filter((cat) => cat !== categoryId),
    }));
  };

  const handleSelectedCategoryChange = (e) => {
    const { value } = e.target;
    if (!value) return;
    const current = selectedProduct?.category || [];
    if (!current.includes(value)) {
      setSelectedProduct((prevProduct) => ({
        ...prevProduct,
        category: [...current, value],
      }));
    }
  };

  const handleSelectedCategoryRemove = (categoryId) => {
    const current = selectedProduct?.category || [];
    setSelectedProduct((prevProduct) => ({
      ...prevProduct,
      category: current.filter((cat) => cat !== categoryId),
    }));
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct({
      ...product,
      category: (product.category || []).map((cat) => cat._id), // normalize to IDs
      isPublic: typeof product.isPublic === 'boolean' ? product.isPublic : false, // ensure boolean
      available_in_apk_launcher: typeof product.available_in_apk_launcher === 'boolean' ? product.available_in_apk_launcher : false,
      cardPaymentEnabled: typeof product.cardPaymentEnabled === 'boolean' ? product.cardPaymentEnabled : false,
    });
  };

  const createProduct = async () => {
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/product/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
        body: JSON.stringify({
          ...newProduct,
          event: newProduct.event?._id || newProduct.event,
          category: newProduct.category,
          description: newProduct.description,
          isPublic: !!newProduct.isPublic, // NEW
          available_in_apk_launcher: !!newProduct.available_in_apk_launcher,
          cardPaymentEnabled: !!newProduct.cardPaymentEnabled,
        }),
      });
      const result = await response.json();
      if (result.success) {
        alert('New Product added successfully!');
        setNewProduct({
          title: '',
          price: '',
          event: '',
          status: true,
          featuredImage: '',
          featuredVideo: '',
          category: [],
          description: '',
          isPublic: false,
          available_in_apk_launcher: false,
          cardPaymentEnabled: false,
        });
        setShowCreateForm(false);
        fetchProducts();
      } else {
        alert('Error adding Product.');
      }
    } catch (error) {
      console.error('Error adding Product:', error);
    }
  };

  const updateProduct = async () => {
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/product/update/${selectedProduct._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
        body: JSON.stringify({
          ...selectedProduct,
          event: selectedProduct.event?._id || selectedProduct.event,
          category: selectedProduct.category,
          description: selectedProduct.description,
          isPublic: !!selectedProduct.isPublic, // NEW
          available_in_apk_launcher: !!selectedProduct.available_in_apk_launcher,
          cardPaymentEnabled: !!selectedProduct.cardPaymentEnabled,
        }),
      });
      const result = await response.json();
      if (result.success) {
        alert('Product updated successfully!');
        setSelectedProduct(null);
        await fetchProducts();
        await fetchEvents();
      } else {
        alert('Error updating Product.');
      }
    } catch (error) {
      console.error('Error updating Product:', error);
    }
  };

  const deleteProduct = async (productId) => {
    const token = localStorage.getItem('auth-token');
    try {
      const response = await fetch(`${backendUrl}/product/delete/${productId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
      });
      const result = await response.json();
      if (result.success) {
        alert('Product deleted successfully!');
        setSelectedProduct(null);
        fetchProducts();
      } else {
        alert('Error deleting Product.');
      }
    } catch (error) {
      console.error('Error deleting Product:', error);
    }
  };

  const sortProducts = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const filteredProducts = React.useMemo(() => {
    let filtered = [...products];

    if (titleFilter) {
      filtered = filtered.filter((product) =>
        (product.title || '').toLowerCase().includes(titleFilter.toLowerCase())
      );
    }

    if (eventFilter) {
      filtered = filtered.filter((product) =>
        (product.event?.title || '').toLowerCase().includes(eventFilter.toLowerCase())
      );
    }

    if (statusFilter) {
      const statusBool = statusFilter === 'Active';
      filtered = filtered.filter((product) => product.status === statusBool);
    }

    if (categoryFilter) {
      filtered = filtered.filter((product) =>
        (product.category || []).some((cat) => cat._id === categoryFilter)
      );
    }

    return filtered;
  }, [products, titleFilter, eventFilter, statusFilter, categoryFilter]);

  const sortedProducts = React.useMemo(() => {
    let sortableProducts = [...filteredProducts];
    if (sortConfig.key) {
      sortableProducts.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        if (sortConfig.key === 'eventTitle') {
          aValue = a.event?.title || '';
          bValue = b.event?.title || '';
        }

        if (sortConfig.key === 'status') {
          aValue = aValue ? 1 : 0;
          bValue = bValue ? 1 : 0;
        }

        if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
        return 0;
      });
    }
    return sortableProducts;
  }, [filteredProducts, sortConfig]);

  return (
    <div className="form-group">
      <h2>Manage Products</h2>

      {selectedProduct ? (
        <div className="form-group">
          <h3>Update Product: {selectedProduct.title}</h3>

          <label htmlFor="title">Title</label>
          <input
            type="text"
            value={selectedProduct.title}
            onChange={handleSelectedProductChange}
            name="title"
          />

          <label htmlFor="price">Price</label>
          <input
            type="number"
            value={selectedProduct.price}
            onChange={handleSelectedProductChange}
            name="price"
          />

          <label htmlFor="event">Event</label>
          <select
            value={selectedProduct.event?._id || selectedProduct.event}
            onChange={handleSelectedProductChange}
            name="event"
          >
            {events.map((event) => (
              <option key={event._id} value={event._id}>
                {event.title}
              </option>
            ))}
          </select>

          <label htmlFor="featuredImage">Featured Image URL</label>
          <input
            type="text"
            value={selectedProduct.featuredImage}
            onChange={handleSelectedProductChange}
            name="featuredImage"
            placeholder="Image URL"
          />

          <label htmlFor="featuredVideo">Featured Video URL</label>
          <input
            type="text"
            value={selectedProduct.featuredVideo}
            onChange={handleSelectedProductChange}
            name="featuredVideo"
            placeholder="Video URL"
          />

          <label htmlFor="status">Status</label>
          <div className="radio-group">
            <input
              type="radio"
              id="statusActive"
              name="status"
              value={true}
              checked={selectedProduct.status === true}
              onChange={() => setSelectedProduct({ ...selectedProduct, status: true })}
            />
            <label
              htmlFor="statusActive"
              className={`radio-label ${selectedProduct.status === true ? 'selected' : ''}`}
            >
              Active
            </label>

            <input
              type="radio"
              id="statusInactive"
              name="status"
              value={false}
              checked={selectedProduct.status === false}
              onChange={() => setSelectedProduct({ ...selectedProduct, status: false })}
            />
            <label
              htmlFor="statusInactive"
              className={`radio-label ${selectedProduct.status === false ? 'selected' : ''}`}
            >
              Inactive
            </label>
          </div>

          {/* NEW: Public toggle */}
          <label htmlFor="isPublic">Anonymous Access</label>
          <div className="radio-group">
            <input
              type="radio"
              id="publicYes"
              name="isPublic"
              value={true}
              checked={selectedProduct.isPublic === true}
              onChange={() => setSelectedProduct({ ...selectedProduct, isPublic: true })}
            />
            <label
              htmlFor="publicYes"
              className={`radio-label ${selectedProduct.isPublic === true ? 'selected' : ''}`}
            >
              Public (no login)
            </label>

            <input
              type="radio"
              id="publicNo"
              name="isPublic"
              value={false}
              checked={selectedProduct.isPublic === false}
              onChange={() => setSelectedProduct({ ...selectedProduct, isPublic: false })}
            />
            <label
              htmlFor="publicNo"
              className={`radio-label ${selectedProduct.isPublic === false ? 'selected' : ''}`}
            >
              Private (login required)
            </label>
          </div>

          <label htmlFor="available_in_apk_launcher">Available in VR Launcher</label>
          <div className="radio-group">
            <input
              type="radio"
              id="launcherYes"
              name="available_in_apk_launcher"
              value={true}
              checked={selectedProduct.available_in_apk_launcher === true}
              onChange={() => setSelectedProduct({ ...selectedProduct, available_in_apk_launcher: true })}
            />
            <label
              htmlFor="launcherYes"
              className={`radio-label ${selectedProduct.available_in_apk_launcher === true ? 'selected' : ''}`}
            >
              Yes
            </label>

            <input
              type="radio"
              id="launcherNo"
              name="available_in_apk_launcher"
              value={false}
              checked={selectedProduct.available_in_apk_launcher === false}
              onChange={() => setSelectedProduct({ ...selectedProduct, available_in_apk_launcher: false })}
            />
            <label
              htmlFor="launcherNo"
              className={`radio-label ${selectedProduct.available_in_apk_launcher === false ? 'selected' : ''}`}
            >
              No
            </label>
          </div>

          <label htmlFor="cardPaymentEnabled">Card payment (Redsys)</label>
          <div className="radio-group">
            <input
              type="radio"
              id="cardPayYes"
              name="cardPaymentEnabled"
              value={true}
              checked={selectedProduct.cardPaymentEnabled === true}
              onChange={() => setSelectedProduct({ ...selectedProduct, cardPaymentEnabled: true })}
            />
            <label
              htmlFor="cardPayYes"
              className={`radio-label ${selectedProduct.cardPaymentEnabled === true ? 'selected' : ''}`}
            >
              Card (Redsys)
            </label>

            <input
              type="radio"
              id="cardPayNo"
              name="cardPaymentEnabled"
              value={false}
              checked={selectedProduct.cardPaymentEnabled === false}
              onChange={() => setSelectedProduct({ ...selectedProduct, cardPaymentEnabled: false })}
            />
            <label
              htmlFor="cardPayNo"
              className={`radio-label ${selectedProduct.cardPaymentEnabled === false ? 'selected' : ''}`}
            >
              Manual approval
            </label>
          </div>

          {/* Show product URL when no login is required */}
          {selectedProduct.isPublic === true && selectedProduct._id && (
            <div style={{
              marginTop: '20px',
              padding: '16px',
              backgroundColor: '#181828',
              borderRadius: '8px',
              border: '1px solid #555'
            }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                marginTop: '0',
                fontWeight: '500',
                color: '#9a51e2',
                fontSize: '14px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Public Product URL
              </label>
              <p style={{
                wordBreak: 'break-all',
                margin: '8px 0',
                padding: '14px 16px',
                backgroundColor: '#23243a',
                borderRadius: '8px',
                fontFamily: 'monospace',
                fontSize: '14px',
                color: '#e2e8f0',
                border: '1px solid #555'
              }}>
                {`${window.location.origin}/product/${selectedProduct._id}`}
              </p>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/product/${selectedProduct._id}`);
                  alert('URL copied to clipboard!');
                }}
                style={{
                  padding: '14px 28px',
                  backgroundColor: '#9333ea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 8px rgba(147, 51, 234, 0.15)',
                  marginTop: '12px'
                }}
              >
                Copy URL
              </button>
            </div>
          )}

          <label>Description</label>
          <textarea
            name="description"
            value={selectedProduct.description}
            onChange={handleSelectedProductChange}
            placeholder="Enter product description"
          />

          <label>Categories</label>
          <div className="checkbox-group">
            <select onChange={handleSelectedCategoryChange}>
              <option value="">Select Category</option>
              {categories.map((category) => (
                <option key={category._id} value={category._id}>
                  {category.name}
                </option>
              ))}
            </select>
            <ul>
              {(selectedProduct.category || []).map((categoryId) => {
                const category = categories.find((cat) => cat._id === categoryId);
                return (
                  <li key={categoryId}>
                    {category?.name}
                    <button onClick={() => handleSelectedCategoryRemove(categoryId)}>Remove</button>
                  </li>
                );
              })}
            </ul>
          </div>

          <button onClick={updateProduct}>Update Product</button>
          <button style={{ backgroundColor: 'red' }} onClick={() => deleteProduct(selectedProduct._id)}>
            Delete Product
          </button>
          <button onClick={() => setSelectedProduct(null)}>Back to List</button>
        </div>
      ) : (
        <>
          {!showCreateForm && (
          <>
          <button onClick={() => setShowCreateForm(true)} style={{ marginBottom: '12px' }}>+ Create New Product</button>
          <div className="filters">
            <label>
              Filter by Title:
              <input
                type="text"
                value={titleFilter}
                onChange={(e) => setTitleFilter(e.target.value)}
                placeholder="Search by title..."
              />
            </label>
            <label>
              Filter by Event:
              <input
                type="text"
                value={eventFilter}
                onChange={(e) => setEventFilter(e.target.value)}
                placeholder="Search by event..."
              />
            </label>
            <label>
              Filter by Status:
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </label>
            <label>
              Filter by Category:
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">All</option>
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <table>
            <thead>
              <tr>
                <th onClick={() => sortProducts('title')}>Title</th>
                <th onClick={() => sortProducts('price')}>Price</th>
                <th onClick={() => sortProducts('eventTitle')}>Event</th>
                <th onClick={() => sortProducts('status')}>Status</th>
                <th>Public</th>{/* NEW column */}
                <th>Launcher</th>
                <th>Payment</th>
              </tr>
            </thead>
            <tbody>
              {sortedProducts.map((product) => (
                <tr
                  key={product._id}
                  onClick={() => handleSelectProduct(product)}
                >
                  <td>{product.title}</td>
                  <td style={{ fontWeight: '600', color: '#ffffff' }}>€{product.price}</td>
                  <td>{product.event?.title || 'N/A'}</td>
                  <td>
                    <span style={{
                      padding: '6px 16px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: product.status
                        ? '#48bb78'
                        : '#f56565',
                      color: 'white',
                      display: 'inline-block',
                      minWidth: '70px',
                      textAlign: 'center'
                    }}>
                      {product.status ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      padding: '6px 16px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: product.isPublic
                        ? '#4299e1'
                        : '#a0aec0',
                      color: 'white',
                      display: 'inline-block',
                      minWidth: '70px',
                      textAlign: 'center'
                    }}>
                      {product.isPublic ? 'Public' : 'Private'}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      padding: '6px 16px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: product.available_in_apk_launcher
                        ? '#4299e1'
                        : '#a0aec0',
                      color: 'white',
                      display: 'inline-block',
                      minWidth: '70px',
                      textAlign: 'center'
                    }}>
                      {product.available_in_apk_launcher ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      padding: '6px 16px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: product.cardPaymentEnabled
                        ? '#38a169'
                        : '#a0aec0',
                      color: 'white',
                      display: 'inline-block',
                      minWidth: '70px',
                      textAlign: 'center'
                    }}>
                      {product.cardPaymentEnabled ? 'Card' : 'Manual'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
          )}
          {showCreateForm && (
          <>
          <h3>Create New Product</h3>
          <div className="form-group">
            <input
              type="text"
              name="title"
              placeholder="Product Title"
              value={newProduct.title}
              onChange={handleNewProductChange}
            />
            <input
              type="number"
              name="price"
              placeholder="Price"
              value={newProduct.price}
              onChange={handleNewProductChange}
            />

            <label htmlFor="event">Event</label>
            <select
              name="event"
              value={newProduct.event?._id || newProduct.event}
              onChange={handleNewProductChange}
            >
              <option value="">Select Event</option>
              {events.map((event) => (
                <option key={event._id} value={event._id}>
                  {event.title}
                </option>
              ))}
            </select>

            <label htmlFor="featuredImage">Featured Image URL</label>
            <input
              type="text"
              name="featuredImage"
              placeholder="Image URL"
              value={newProduct.featuredImage}
              onChange={handleNewProductChange}
            />

            <label htmlFor="featuredVideo">Featured Video URL</label>
            <input
              type="text"
              name="featuredVideo"
              placeholder="Video URL"
              value={newProduct.featuredVideo}
              onChange={handleNewProductChange}
            />

            <label htmlFor="status">Status</label>
            <div className="radio-group">
              <input
                type="radio"
                id="statusActive"
                name="status"
                value={true}
                checked={newProduct.status === true}
                onChange={() => setNewProduct({ ...newProduct, status: true })}
              />
              <label
                htmlFor="statusActive"
                className={`radio-label ${newProduct.status === true ? 'selected' : ''}`}
              >
                Active
              </label>

              <input
                type="radio"
                id="statusInactive"
                name="status"
                value={false}
                checked={newProduct.status === false}
                onChange={() => setNewProduct({ ...newProduct, status: false })}
              />
              <label
                htmlFor="statusInactive"
                className={`radio-label ${newProduct.status === false ? 'selected' : ''}`}
              >
                Inactive
              </label>
            </div>

            {/* NEW: Public toggle on create */}
            <label htmlFor="isPublic">Anonymous Access</label>
            <div className="radio-group">
              <input
                type="radio"
                id="newPublicYes"
                name="isPublic"
                value={true}
                checked={newProduct.isPublic === true}
                onChange={() => setNewProduct({ ...newProduct, isPublic: true })}
              />
              <label
                htmlFor="newPublicYes"
                className={`radio-label ${newProduct.isPublic === true ? 'selected' : ''}`}
              >
                Public (no login)
              </label>

              <input
                type="radio"
                id="newPublicNo"
                name="isPublic"
                value={false}
                checked={newProduct.isPublic === false}
                onChange={() => setNewProduct({ ...newProduct, isPublic: false })}
              />
              <label
                htmlFor="newPublicNo"
                className={`radio-label ${newProduct.isPublic === false ? 'selected' : ''}`}
              >
                Private (login required)
              </label>
            </div>

            <label htmlFor="newAvailableInLauncher">Available in VR Launcher</label>
            <div className="radio-group">
              <input
                type="radio"
                id="newLauncherYes"
                name="available_in_apk_launcher"
                value={true}
                checked={newProduct.available_in_apk_launcher === true}
                onChange={() => setNewProduct({ ...newProduct, available_in_apk_launcher: true })}
              />
              <label
                htmlFor="newLauncherYes"
                className={`radio-label ${newProduct.available_in_apk_launcher === true ? 'selected' : ''}`}
              >
                Yes
              </label>

              <input
                type="radio"
                id="newLauncherNo"
                name="available_in_apk_launcher"
                value={false}
                checked={newProduct.available_in_apk_launcher === false}
                onChange={() => setNewProduct({ ...newProduct, available_in_apk_launcher: false })}
              />
              <label
                htmlFor="newLauncherNo"
                className={`radio-label ${newProduct.available_in_apk_launcher === false ? 'selected' : ''}`}
              >
                No
              </label>
            </div>

            <label htmlFor="newCardPaymentEnabled">Card payment (Redsys)</label>
            <div className="radio-group">
              <input
                type="radio"
                id="newCardPayYes"
                name="cardPaymentEnabled"
                value={true}
                checked={newProduct.cardPaymentEnabled === true}
                onChange={() => setNewProduct({ ...newProduct, cardPaymentEnabled: true })}
              />
              <label
                htmlFor="newCardPayYes"
                className={`radio-label ${newProduct.cardPaymentEnabled === true ? 'selected' : ''}`}
              >
                Card (Redsys)
              </label>

              <input
                type="radio"
                id="newCardPayNo"
                name="cardPaymentEnabled"
                value={false}
                checked={newProduct.cardPaymentEnabled === false}
                onChange={() => setNewProduct({ ...newProduct, cardPaymentEnabled: false })}
              />
              <label
                htmlFor="newCardPayNo"
                className={`radio-label ${newProduct.cardPaymentEnabled === false ? 'selected' : ''}`}
              >
                Manual approval
              </label>
            </div>

            {/* Show message when public product is being created */}
            {newProduct.isPublic === true && (
              <div style={{
                marginTop: '20px',
                padding: '16px',
                backgroundColor: '#181828',
                borderRadius: '8px',
                border: '1px solid #555'
              }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  marginTop: '0',
                  fontWeight: '500',
                  color: '#9a51e2',
                  fontSize: '14px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Public Product URL
                </label>
                <p style={{
                  margin: '8px 0',
                  color: '#cbd5e0',
                  fontSize: '14px',
                  padding: '12px 0'
                }}>
                  A unique product URL will be generated and displayed here after you create this product.
                </p>
              </div>
            )}

            <label>Description</label>
            <textarea
              name="description"
              value={newProduct.description}
              onChange={handleNewProductChange}
              placeholder="Enter product description"
            />

            <label>Categories</label>
            <div className="checkbox-group">
              <select onChange={handleCategoryChange}>
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
              <ul>
                {newProduct.category.map((categoryId) => {
                  const category = categories.find((cat) => cat._id === categoryId);
                  return (
                    <li key={categoryId}>
                      {category?.name}
                      <button onClick={() => handleCategoryRemove(categoryId)}>Remove</button>
                    </li>
                  );
                })}
              </ul>
            </div>

            <button onClick={createProduct}>Add Product</button>
          </div>
          <button onClick={() => setShowCreateForm(false)}>Back to List</button>
          </>
          )}
        </>
      )}
    </div>
  );
};

export default ManageProducts;
